<template>
  <div class="report-dashboard">
    <div v-if="!has_data" class="empty text-center grey--text pa-6">
      <v-icon size="42" color="grey lighten-1">mdi-chart-box-outline</v-icon>
      <div class="mt-2">No chart data for the selected range.</div>
    </div>

    <template v-else>
      <!-- KPI cards -->
      <v-row dense>
        <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="4" md="2">
          <base-stat-tile
            :title="kpi.label"
            :value="kpi.value"
            :icon="kpi.icon"
            :accent="kpi.color"
          />
        </v-col>
      </v-row>

      <v-row dense class="mt-1">
        <v-col cols="12" md="8">
          <v-card class="pa-3" outlined>
            <div class="chart-title">Distance per day (km)</div>
            <div class="chart-box"><canvas ref="distanceChart"></canvas></div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="pa-3" outlined>
            <div class="chart-title">Driving vs idle time</div>
            <div class="chart-box"><canvas ref="timeChart"></canvas></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-1">
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="chart-title">Trips &amp; stoppages per day</div>
            <div class="chart-box"><canvas ref="tripsChart"></canvas></div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-3" outlined>
            <div class="chart-title">
              Speed distribution (KPH)
              <span class="chart-sub">{{ rawPoints.length }} raw points</span>
            </div>
            <div class="chart-box"><canvas ref="speedChart"></canvas></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-1">
        <v-col cols="12">
          <v-card class="pa-3" outlined>
            <div class="chart-title">Top speed per day (KPH)</div>
            <div class="chart-box"><canvas ref="topSpeedChart"></canvas></div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import Chart from "chart.js";

const COLORS = {
  primary: "#1c92e1",
  secondary: "#fe9106",
  success: "#4CAF50",
  danger: "#e24b4a",
  grey: "#9E9E9E",
  purple: "#9C27B0",
};

export default {
  name: "ReportDashboard",
  props: {
    daily: { type: Array, default: () => [] },
    rawPoints: { type: Array, default: () => [] },
  },
  data() {
    return {};
  },
  computed: {
    dayLabels() {
      return this.daily.map((d) => String(d.summary_date || "").slice(5));
    },
    totals() {
      const t = { distance: 0, driving: 0, idle: 0, trips: 0, stops: 0, maxSpeed: 0 };
      for (const d of this.daily) {
        t.distance += Number(d.total_distance_km) || 0;
        t.driving += Number(d.total_driving_sec) || 0;
        t.idle += Number(d.total_idle_sec) || 0;
        t.trips += Number(d.trip_count) || 0;
        t.stops += Number(d.stop_count) || 0;
        t.maxSpeed = Math.max(t.maxSpeed, Number(d.max_speed_kmh) || 0);
      }
      return t;
    },
    avgSpeed() {
      const t = this.totals;
      return t.driving > 0 ? t.distance / (t.driving / 3600) : 0;
    },
    has_data() {
      return this.daily.length > 0 || this.rawPoints.length > 0;
    },
    kpis() {
      const t = this.totals;
      return [
        { label: "Distance", value: this.fmtNum(t.distance, 1) + " km", icon: "mdi-map-marker-distance", color: COLORS.primary },
        { label: "Driving time", value: this.formatDuration(t.driving), icon: "mdi-timer-outline", color: COLORS.success },
        { label: "Idle time", value: this.formatDuration(t.idle), icon: "mdi-timer-pause-outline", color: COLORS.secondary },
        { label: "Trips", value: this.fmtNum(t.trips), icon: "mdi-road-variant", color: COLORS.primary },
        { label: "Stoppages", value: this.fmtNum(t.stops), icon: "mdi-car-brake-parking", color: COLORS.purple },
        { label: "Avg / Top speed", value: this.fmtNum(this.avgSpeed, 0) + " / " + this.fmtNum(t.maxSpeed, 0), icon: "mdi-speedometer", color: COLORS.danger },
      ];
    },
  },
  created() {
    // Keep Chart instances off the reactive data graph.
    this._charts = {};
  },
  mounted() {
    this.$nextTick(() => this.renderCharts());
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  watch: {
    daily: { handler() { this.$nextTick(() => this.renderCharts()); }, deep: true },
    rawPoints: { handler() { this.$nextTick(() => this.renderCharts()); }, deep: true },
  },
  methods: {
    fmtNum(value, decimals = 0) {
      return (Number(value) || 0).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    formatDuration(seconds) {
      const secs = Math.max(0, Math.round(Number(seconds) || 0));
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      if (h > 0) return h + "h " + m + "m";
      if (m > 0) return m + "m";
      return secs + "s";
    },
    baseOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          labels: { boxWidth: 12, fontSize: 10 },
        },
        tooltips: { mode: "index", intersect: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 10, maxRotation: 0, autoSkip: true } }],
          yAxes: [{ ticks: { beginAtZero: true, fontSize: 10 }, gridLines: { color: "rgba(0,0,0,0.06)" } }],
        },
      };
    },
    destroyCharts() {
      Object.keys(this._charts).forEach((key) => {
        if (this._charts[key]) {
          this._charts[key].destroy();
          this._charts[key] = null;
        }
      });
    },
    renderCharts() {
      if (!Chart) return;
      this.destroyCharts();
      if (this.daily.length) {
        this.buildDistanceChart();
        this.buildTripsChart();
        this.buildTimeChart();
        this.buildTopSpeedChart();
      }
      if (this.rawPoints.length) {
        this.buildSpeedChart();
      }
    },
    buildDistanceChart() {
      const el = this.$refs.distanceChart;
      if (!el) return;
      const data = this.daily.map((d) => Number(d.total_distance_km) || 0);
      const options = this.baseOptions();
      options.tooltips.callbacks = {
        label: (item) => " " + this.fmtNum(item.yLabel, 2) + " km",
      };
      this._charts.distance = new Chart(el, {
        type: "bar",
        data: {
          labels: this.dayLabels,
          datasets: [
            {
              label: "Distance (km)",
              data,
              backgroundColor: COLORS.primary,
              borderRadius: 3,
              maxBarThickness: 26,
            },
          ],
        },
        options,
      });
    },
    buildTripsChart() {
      const el = this.$refs.tripsChart;
      if (!el) return;
      const options = this.baseOptions();
      options.legend.display = true;
      options.legend.position = "top";
      this._charts.trips = new Chart(el, {
        type: "bar",
        data: {
          labels: this.dayLabels,
          datasets: [
            {
              label: "Trips",
              data: this.daily.map((d) => Number(d.trip_count) || 0),
              backgroundColor: COLORS.success,
              maxBarThickness: 20,
            },
            {
              label: "Stoppages",
              data: this.daily.map((d) => Number(d.stop_count) || 0),
              backgroundColor: COLORS.secondary,
              maxBarThickness: 20,
            },
          ],
        },
        options,
      });
    },
    buildTimeChart() {
      const el = this.$refs.timeChart;
      if (!el) return;
      const t = this.totals;
      const options = this.baseOptions();
      options.legend.display = true;
      options.legend.position = "bottom";
      options.scales = {};
      options.tooltips.callbacks = {
        label: (item, data) => {
          const value = data.datasets[item.datasetIndex].data[item.index];
          return " " + data.labels[item.index] + ": " + this.formatDuration(value);
        },
      };
      this._charts.time = new Chart(el, {
        type: "doughnut",
        data: {
          labels: ["Driving", "Idle"],
          datasets: [
            {
              data: [t.driving, t.idle],
              backgroundColor: [COLORS.success, COLORS.secondary],
              borderWidth: 0,
            },
          ],
        },
        options,
      });
    },
    buildTopSpeedChart() {
      const el = this.$refs.topSpeedChart;
      if (!el) return;
      const options = this.baseOptions();
      options.tooltips.callbacks = {
        label: (item) => " " + this.fmtNum(item.yLabel, 0) + " KPH",
      };
      this._charts.topSpeed = new Chart(el, {
        type: "line",
        data: {
          labels: this.dayLabels,
          datasets: [
            {
              label: "Top speed (KPH)",
              data: this.daily.map((d) => Number(d.max_speed_kmh) || 0),
              borderColor: COLORS.danger,
              backgroundColor: "rgba(226,75,74,0.12)",
              fill: true,
              pointRadius: 2,
              lineTension: 0.3,
            },
          ],
        },
        options,
      });
    },
    buildSpeedChart() {
      const el = this.$refs.speedChart;
      if (!el) return;
      const labels = ["0", "1-20", "21-40", "41-60", "61-80", "81-100", "100+"];
      const bins = new Array(labels.length).fill(0);
      for (const p of this.rawPoints) {
        const s = Number(p[5]) || 0;
        let i = 0;
        if (s <= 0) i = 0;
        else if (s <= 20) i = 1;
        else if (s <= 40) i = 2;
        else if (s <= 60) i = 3;
        else if (s <= 80) i = 4;
        else if (s <= 100) i = 5;
        else i = 6;
        bins[i] += 1;
      }
      const options = this.baseOptions();
      options.tooltips.callbacks = {
        label: (item) => " " + this.fmtNum(item.yLabel) + " points",
      };
      this._charts.speed = new Chart(el, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Points",
              data: bins,
              backgroundColor: [
                COLORS.grey,
                COLORS.success,
                COLORS.primary,
                COLORS.primary,
                COLORS.secondary,
                COLORS.danger,
                COLORS.purple,
              ],
              maxBarThickness: 34,
            },
          ],
        },
        options,
      });
    },
  },
};
</script>

<style scoped>
.chart-title {
  font-size: 12px;
  font-weight: 600;
  color: #424242;
  margin-bottom: 8px;
}
.chart-sub {
  font-weight: 400;
  color: #9e9e9e;
  margin-left: 6px;
}
.chart-box {
  position: relative;
  height: 240px;
  width: 100%;
}
</style>
